import React, { useState, useEffect, useMemo } from 'react';
import { TextField, InputAdornment, Box, Typography, Select } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SearchIcon from '@mui/icons-material/Search';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { matchKorean, getChosung } from '../../../../utils/methods';
import { useQueryClient } from '@tanstack/react-query';
import FavoriteStar from '../../../common/FavoriteStar';
import { FAVORITE_TARGET } from '../../../../api/favorite';

import SellerShareSelect from '../../../common/SellerShareSelect';
import RegisterTypeToggle from './RegisterTypeToggle';
import { FormInputText } from '../../../../form-components/FormInputText';
import { FormInputDate } from '../../../../form-components/FormInputDate';
import CommonSubscriptionBadge from '../../../../form-components/CommonSubscriptionBadge';
import PointUsageFields from './PointUsageFields';

import {
  FormInputWrapper,
  DropdownOptionWrapper,
  DropdownOptionLabel,
  DropdownStatusBadge,
  textInputStyle,
  disabledInputStyle,
  dateInputStyleFlex,
  ErrorFieldWrapper,
  DateSeparator,
  ContentSection,
  FormFieldGroup,
  FormFieldGroupHalf,
  FormFieldRow,
  FormFieldLabel,
  BasePriceHint,
  ToggleRow,
  ToggleWrapper,
  ToggleText,
  CustomSwitch,
} from './EnterSaleInfoModalStyled';

// InputAdornment 스타일
const adornmentStyle = { color: '#737373', fontSize: '13px', fontWeight: 500 };

const EnterSaleInfoModalPassSection = ({
  control,
  setValue,
  watch,
  branchManager,
  coursePass,
  ticketPassPrice,
  ticketPassDiscountAmount,
  ticketPassFinalPrice,
  ticketPassOutstandingPrice,
  passUsagePeriod,
  passUsagePeriodType,
  getPassPrice,
  changeTicketPassPrice,
  passBasePrice,
  changeTicketPassFinalPrice,
  changeTicketPassOutstandingPrice,
  changeTicketPassPaidPrice,
  formatInteger,
  errorFields,
  setErrorFields,
  showExpiredProducts,
  setShowExpiredProducts,
  isPointUsageEnabled,
  availablePoint,
  minUsePoint,
  usablePointLimit,
}) => {
  const { t } = useTranslation();
  const [passPanelOpen, setPassPanelOpen] = useState(false);
  const [passSearch, setPassSearch] = useState('');
  const [sportTypeFilter, setSportTypeFilter] = useState('ALL');
  const [trialFilter, setTrialFilter] = useState(false);

  const sportTypes = useMemo(() => {
    const types = new Set();
    (coursePass || []).forEach(p => (p.sportTypes || []).forEach(s => types.add(s)));
    return [...types];
  }, [coursePass]);

  const queryClient = useQueryClient();

  const filteredCoursePass = useMemo(() => {
    let filtered = coursePass || [];
    if (!showExpiredProducts) {
      filtered = filtered.filter(pass => pass.status === 'ON_SALE' || pass.status === 'APPROVED');
    }
    if (sportTypeFilter !== 'ALL') {
      filtered = filtered.filter(pass => (pass.sportTypes || []).includes(sportTypeFilter));
    }
    if (trialFilter) {
      filtered = filtered.filter(pass => pass.sourceType === 'TRIAL_TICKET');
    }
    if (passSearch.trim()) {
      filtered = filtered.filter(pass => matchKorean(pass.label || '', passSearch.trim()));
    }
    // 판매중 먼저 + ㄱㄴㄷ → 영어 → 숫자 → 특문
    const charOrder = c => {
      if (/[가-힣ㄱ-ㅎㅏ-ㅣ]/.test(c)) return 0;
      if (/[a-zA-Z]/.test(c)) return 1;
      if (/[0-9]/.test(c)) return 2;
      return 3;
    };
    filtered.sort((a, b) => {
      if (!!a.favorite !== !!b.favorite) return a.favorite ? -1 : 1;
      const aActive = a.status === 'ON_SALE' || a.status === 'APPROVED';
      const bActive = b.status === 'ON_SALE' || b.status === 'APPROVED';
      if (aActive !== bActive) return bActive - aActive;
      const aFirst = (a.label || '')[0] || '';
      const bFirst = (b.label || '')[0] || '';
      const orderDiff = charOrder(aFirst) - charOrder(bFirst);
      if (orderDiff !== 0) return orderDiff;
      return (a.label || '').localeCompare(b.label || '', 'ko');
    });
    return filtered;
  }, [coursePass, showExpiredProducts, sportTypeFilter, trialFilter, passSearch]);

  // 시작일 변경 시 종료일 자동 계산
  const watchedStartDate = watch('courseStartDate');

  useEffect(() => {
    if (!watchedStartDate || !passUsagePeriod || !passUsagePeriodType) return;

    let endDate;
    if (passUsagePeriodType === 'MONTH') {
      endDate = dayjs(watchedStartDate).add(passUsagePeriod, 'month').subtract(1, 'day').toDate();
    } else if (passUsagePeriodType === 'DAY') {
      endDate = dayjs(watchedStartDate).add(passUsagePeriod, 'day').subtract(1, 'day').toDate();
    }

    if (endDate) {
      setValue('courseEndDate', endDate);
    }
  }, [watchedStartDate, passUsagePeriod, passUsagePeriodType, setValue]);

  return (
    <ContentSection>
      {/* #124 신규등록/재등록 — 라디오 카드. 기본값은 서버 자동 판정, 바꾸면 그 값이 저장된다 */}
      <RegisterTypeToggle control={control} setValue={setValue} />

      {/* 판매일시 + 판매 담당자 */}
      <FormFieldRow>
        <FormFieldGroupHalf>
          <FormFieldLabel>{t('판매일시')}</FormFieldLabel>
          <FormInputDate
            name="saleDate"
            control={control}
            placeholder={t('판매일(오늘)')}
            renderInput={params => (
              <TextField color="info" size="small" variant="outlined" {...params} sx={{ ...dateInputStyleFlex, width: '100%' }} />
            )}
          />
        </FormFieldGroupHalf>
        <FormFieldGroupHalf>
          <FormFieldLabel>{t('판매 담당자')}</FormFieldLabel>
          <ErrorFieldWrapper hasError={errorFields?.branchManager}>
            {/* #122 판매 담당자 N명 + 귀속 비율 — 담당 강사 다중 선택과 같은 구성 */}
            <SellerShareSelect
              control={control}
              setValue={setValue}
              name="branchManager"
              sharesName="sellerShares"
              options={branchManager}
              placeholder={t('담당자 선택')}
              onChange={() => setErrorFields(prev => ({ ...prev, branchManager: false }))}
            />
          </ErrorFieldWrapper>
        </FormFieldGroupHalf>
      </FormFieldRow>

      {/* 이용권 선택 with toggle */}
      <FormFieldGroup>
        <ToggleRow>
          <FormFieldLabel>{t('이용권 선택')}</FormFieldLabel>
          <ToggleWrapper>
            <ToggleText>{t('판매종료 상품 노출')}</ToggleText>
            <CustomSwitch checked={showExpiredProducts} onChange={e => setShowExpiredProducts(e.target.checked)} />
          </ToggleWrapper>
        </ToggleRow>
        <ErrorFieldWrapper hasError={errorFields?.coursePass}>
          <Box sx={{ width: '100%' }}>
            {/* 헤더 */}
            <Box
              onClick={() => { setPassPanelOpen(prev => !prev); setPassSearch(''); }}
              sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                height: '44px', px: '14px',
                border: passPanelOpen ? '1.5px solid #0F7F85' : '1px solid #c4c4c4',
                borderRadius: passPanelOpen ? '6px 6px 0 0' : '6px',
                cursor: 'pointer',
                backgroundColor: watch('coursePass') ? '#f0f8f8' : '#fff',
                transition: 'all 0.15s ease',
                '&:hover': { borderColor: '#0F7F85' },
              }}>
              <Typography sx={{ fontSize: '14px', fontWeight: watch('coursePass') ? 500 : 400, color: watch('coursePass') ? '#0F7F85' : '#999' }}>
                {watch('coursePass')
                  ? (coursePass || []).find(p => p.value === watch('coursePass'))?.label || '선택됨'
                  : '이용권을 선택해 주세요'}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '24px', height: '24px', borderRadius: '50%', backgroundColor: passPanelOpen ? '#0F7F85' : 'transparent', transition: 'all 0.15s ease' }}>
                {passPanelOpen ? <KeyboardArrowUpIcon sx={{ color: '#fff', fontSize: 18 }} /> : <KeyboardArrowDownIcon sx={{ color: '#999', fontSize: 18 }} />}
              </Box>
            </Box>

            {/* 패널 */}
            {passPanelOpen && (
              <Box sx={{ border: '1.5px solid #0F7F85', borderTop: 'none', borderRadius: '0 0 6px 6px', maxHeight: '500px', display: 'flex', flexDirection: 'column' }}>
                {/* 검색 */}
                <Box sx={{ display: 'flex', gap: '8px', p: '10px 12px', borderBottom: '1px solid #eee' }}>
                  <TextField
                    size="small"
                    placeholder="이용권 검색"
                    value={passSearch}
                    onChange={e => setPassSearch(e.target.value)}
                    onClick={e => e.stopPropagation()}
                    autoFocus
                    sx={{ flex: 1, '& .MuiOutlinedInput-root': { height: '36px', fontSize: '13px' } }}
                    InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: '#999' }} /></InputAdornment> }}
                  />
                </Box>

                {/* 종목 + 일일권 필터 */}
                {(sportTypes.length > 1 || (coursePass || []).some(p => p.sourceType === 'TRIAL_TICKET')) && (
                  <Box sx={{ display: 'flex', gap: '6px', px: '12px', py: '8px', borderBottom: '1px solid #eee', flexWrap: 'wrap', alignItems: 'center' }}>
                    {sportTypes.length > 1 && (
                      <>
                        <Box onClick={() => setSportTypeFilter('ALL')} sx={{ px: '10px', py: '4px', borderRadius: '14px', fontSize: '12px', fontWeight: 600, cursor: 'pointer', backgroundColor: sportTypeFilter === 'ALL' ? '#4ab3bc' : '#f5f5f5', color: sportTypeFilter === 'ALL' ? '#fff' : '#666', '&:hover': { opacity: 0.8 } }}>
                          전체
                        </Box>
                        {sportTypes.map(type => (
                          <Box key={type} onClick={() => setSportTypeFilter(type)} sx={{ px: '10px', py: '4px', borderRadius: '14px', fontSize: '12px', fontWeight: 600, cursor: 'pointer', backgroundColor: sportTypeFilter === type ? '#4ab3bc' : '#f5f5f5', color: sportTypeFilter === type ? '#fff' : '#666', '&:hover': { opacity: 0.8 } }}>
                            {t(type)}
                          </Box>
                        ))}
                        {(coursePass || []).some(p => p.sourceType === 'TRIAL_TICKET') && (
                          <Box sx={{ width: '1px', height: '24px', backgroundColor: '#ddd', mx: '2px' }} />
                        )}
                      </>
                    )}
                    {(coursePass || []).some(p => p.sourceType === 'TRIAL_TICKET') && (
                      <Box
                        onClick={() => setTrialFilter(prev => !prev)}
                        sx={{
                          px: '10px', py: '4px', borderRadius: '14px', fontSize: '12px', fontWeight: 600, cursor: 'pointer',
                          backgroundColor: trialFilter ? '#e67e22' : '#f5f5f5',
                          color: trialFilter ? '#fff' : '#666',
                          '&:hover': { opacity: 0.8 },
                        }}>
                        일일권
                      </Box>
                    )}
                  </Box>
                )}

                {/* 리스트 */}
                <Box sx={{ overflowY: 'auto', flex: 1 }}>
                  {filteredCoursePass.length === 0 ? (
                    <Box sx={{ p: '24px', textAlign: 'center' }}>
                      <Typography sx={{ fontSize: '13px', color: '#999' }}>검색 결과가 없습니다</Typography>
                    </Box>
                  ) : (
                    (() => {
                      let lastCho = '';
                      return filteredCoursePass.map(pass => {
                        const firstChar = (pass.label || '')[0] || '';
                        // 즐겨찾기는 초성 대신 '즐겨찾기' 그룹으로 묶어 최상단에 보인다
                        const cho = pass.favorite ? '★ 즐겨찾기' : getChosung(firstChar);
                        const showHeader = cho !== lastCho;
                        lastCho = cho;
                        const isSelected = watch('coursePass') === pass.value;
                        const isEnded = pass.status !== 'ON_SALE' && pass.status !== 'APPROVED';
                        return (
                          <React.Fragment key={pass.value}>
                            {showHeader && (
                              <Box sx={{ px: '14px', py: '4px', backgroundColor: '#f7f7f7', borderBottom: '1px solid #eee' }}>
                                <Typography sx={{ fontSize: '12px', fontWeight: 700, color: '#999' }}>{cho}</Typography>
                              </Box>
                            )}
                            <Box
                              onClick={() => {
                                setValue('coursePass', pass.value);
                                getPassPrice(pass.value);
                                setErrorFields(prev => ({ ...prev, coursePass: false }));
                                setPassPanelOpen(false);
                                setPassSearch('');
                              }}
                              sx={{
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                px: '14px', py: '10px', cursor: 'pointer',
                                backgroundColor: isSelected ? '#f0f8f8' : isEnded ? '#fafafa' : '#fff',
                                borderBottom: '1px solid #f5f5f5',
                                '&:hover': { backgroundColor: isSelected ? '#e6f5f5' : '#f5f5f5' },
                                '&:last-child': { borderBottom: 'none' },
                              }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', flex: 1, minWidth: 0 }}>
                                {/* 판매등록 전용 즐겨찾기(SALE_*) — 토글 후 옵션 목록을 다시 불러 그룹을 재정렬한다 */}
                                <FavoriteStar
                                  targetType={pass.sourceType === 'TRIAL_TICKET' ? FAVORITE_TARGET.SALE_TRIAL : FAVORITE_TARGET.SALE_PASS}
                                  targetId={pass.value}
                                  favorite={pass.favorite}
                                  onChanged={() => queryClient.invalidateQueries({ queryKey: ['coursePass'] })}
                                />
                                {isSelected && <CheckCircleIcon sx={{ fontSize: 18, color: '#0F7F85', flexShrink: 0 }} />}
                                {pass.subscriptionYn && <CommonSubscriptionBadge />}
                                <Typography sx={{ fontSize: '13px', color: isEnded ? '#bbb' : '#333', fontWeight: isSelected ? 600 : 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                  {pass.label?.split(' ').slice(0, -1).join(' ') || pass.label}
                                </Typography>
                                <Typography sx={{ fontSize: '12px', color: '#999', flexShrink: 0 }}>
                                  {pass.label?.split(' ').slice(-1)[0] || ''}
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '5px', flexShrink: 0, ml: '8px' }}>
                                <Box sx={{ display: 'flex', gap: '4px' }}>
                                  {pass.sourceType === 'TRIAL_TICKET' && (
                                    <Typography sx={{ fontSize: '10px', fontWeight: 600, px: '6px', py: '1px', borderRadius: '3px', color: '#e67e22', backgroundColor: '#fef5e7', border: '1px solid #f5d9a8' }}>
                                      일일권
                                    </Typography>
                                  )}
                                  {(pass.sportTypes || []).map(st => (
                                    <Typography key={st} sx={{ fontSize: '10px', fontWeight: 600, px: '6px', py: '1px', borderRadius: '3px', color: '#4ab3bc', backgroundColor: '#edf8f9', border: '1px solid #d0edf0' }}>
                                      {t(st)}
                                    </Typography>
                                  ))}
                                </Box>
                                {/* 운동 라벨 아래 상품 금액 — 할인이 있으면 정가에 취소선을 긋고 판매가를 함께 보여준다. */}
                                {pass.regularPrice > 0 && (
                                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '4px' }}>
                                    {pass.finalPrice != null && pass.finalPrice !== pass.regularPrice && (
                                      <Typography sx={{ fontSize: '11px', lineHeight: 1, color: '#bbb', textDecoration: 'line-through' }}>
                                        {formatInteger(pass.regularPrice)}원
                                      </Typography>
                                    )}
                                    <Typography sx={{ fontSize: '12px', lineHeight: 1, fontWeight: 600, color: isEnded ? '#bbb' : '#0F7F85' }}>
                                      {formatInteger(pass.finalPrice ?? pass.regularPrice)}원
                                    </Typography>
                                  </Box>
                                )}
                              </Box>
                            </Box>
                          </React.Fragment>
                        );
                      });
                    })()
                  )}
                </Box>
              </Box>
            )}
          </Box>
        </ErrorFieldWrapper>
      </FormFieldGroup>

      {/* 판매금액 / 할인금액 (2 columns) */}
      <FormFieldRow>
        <FormFieldGroupHalf>
          <FormFieldLabel>
            {t('판매금액')}
            {passBasePrice > 0 && formatInteger(ticketPassPrice) !== formatInteger(passBasePrice) && (
              <BasePriceHint>정가 {formatInteger(passBasePrice)}원</BasePriceHint>
            )}
          </FormFieldLabel>
          <FormInputText
            control={control}
            onChange={e => changeTicketPassPrice(e.target.value)}
            disabled={!watch('coursePass')}
            color="info"
            sx={!watch('coursePass') ? disabledInputStyle : textInputStyle}
            variant="outlined"
            name="passPrice"
            size="small"
            staticValue={formatInteger(ticketPassPrice)}
            placeholder={t('0')}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end" sx={adornmentStyle}>
                  {t('원')}
                </InputAdornment>
              ),
            }}
          />
        </FormFieldGroupHalf>
        <FormFieldGroupHalf>
          <FormFieldLabel>{t('할인금액')}</FormFieldLabel>
          <FormInputText
            control={control}
            onChange={e => changeTicketPassFinalPrice(e.target.value)}
            color="info"
            sx={textInputStyle}
            variant="outlined"
            name="ticketPassDiscountAmount"
            size="small"
            value={ticketPassDiscountAmount}
            placeholder={t('0')}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end" sx={adornmentStyle}>
                  {t('원')}
                </InputAdornment>
              ),
            }}
          />
        </FormFieldGroupHalf>
      </FormFieldRow>

      {/* 보유 포인트 / 사용할 포인트 (정식회원 + 지점 포인트 사용 설정 on 일 때만) */}
      <PointUsageFields
        isVisible={isPointUsageEnabled}
        control={control}
        availablePoint={availablePoint}
        minUsePoint={minUsePoint}
        usablePointLimit={usablePointLimit}
        errorFields={errorFields}
        setErrorFields={setErrorFields}
        formatInteger={formatInteger}
      />

      {/* 미수금 / 결제금액 (2 columns) */}
      <FormFieldRow>
        <FormFieldGroupHalf>
          <FormFieldLabel>{t('미수금')}</FormFieldLabel>
          <FormInputText
            control={control}
            onChange={e => changeTicketPassOutstandingPrice(e.target.value)}
            color="info"
            sx={{
              ...textInputStyle,
              '& .MuiOutlinedInput-input': {
                ...textInputStyle['& .MuiOutlinedInput-input'],
                color: '#ee735a !important',
              },
            }}
            variant="outlined"
            name="ticketPassOutstandingPrice"
            size="small"
            value={formatInteger(ticketPassOutstandingPrice)}
            placeholder={t('0')}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end" sx={adornmentStyle}>
                  {t('원')}
                </InputAdornment>
              ),
            }}
          />
        </FormFieldGroupHalf>
        <FormFieldGroupHalf>
          <FormFieldLabel>{t('결제금액')}</FormFieldLabel>
          <FormInputText
            control={control}
            onChange={e => changeTicketPassPaidPrice(e.target.value)}
            color="info"
            sx={textInputStyle}
            variant="outlined"
            name="ticketPassFinalPrice"
            size="small"
            value={formatInteger(ticketPassFinalPrice)}
            placeholder={t('0')}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end" sx={adornmentStyle}>
                  {t('원')}
                </InputAdornment>
              ),
            }}
          />
        </FormFieldGroupHalf>
      </FormFieldRow>

      {/* 이용기간 */}
      <FormFieldGroup>
        <FormFieldLabel>{t('이용기간')}</FormFieldLabel>
        <FormInputWrapper>
          <ErrorFieldWrapper hasError={errorFields?.courseStartDate}>
            <FormInputDate
              name="courseStartDate"
              control={control}
              rules={{ required: true }}
              placeholder={t('오늘 날짜')}
              onChange={() => setErrorFields(prev => ({ ...prev, courseStartDate: false }))}
              renderInput={params => (
                <TextField color="info" size="small" variant="outlined" {...params} sx={dateInputStyleFlex} />
              )}
            />
          </ErrorFieldWrapper>
          <DateSeparator>~</DateSeparator>
          <ErrorFieldWrapper hasError={errorFields?.courseEndDate}>
            <FormInputDate
              name="courseEndDate"
              control={control}
              placeholder={t('자동')}
              onChange={() => setErrorFields(prev => ({ ...prev, courseEndDate: false }))}
              renderInput={params => (
                <TextField color="info" size="small" variant="outlined" {...params} sx={dateInputStyleFlex} />
              )}
            />
          </ErrorFieldWrapper>
        </FormInputWrapper>
      </FormFieldGroup>
    </ContentSection>
  );
};

export default EnterSaleInfoModalPassSection;
